import { Analytics } from '@cobliteam/analytics';
import type { LocalUserPreferences } from '@cobliteam/api-lib';
import { useLocalStorage } from '@cobliteam/hooks';
import type { TFunction } from '@cobliteam/i18n';
import type { Permission } from '@cobliteam/permissions';
import { usePermissions } from '@cobliteam/permissions';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressBar from '../../../../ProgressBar';
import ActivationTaskCard from '../ActivationTaskCard';
import type { ActivationTasksTypes, Category, Task } from '../ActivationTasksCommon';
import CategoryAccordion from '../CategoryAccordion';
import { getOnboardingTaskConfiguration } from './OnboardingActivationTasksTypes';

interface OnboardingActivationTaskProps {
  initialCheckedActivationTasks: ActivationTasksTypes[];
  onFinishOnboarding: () => void;
}

interface FinishedContentProps {
  t: TFunction;
  onGoBackToTour: () => void;
  onFinishOnboarding: () => void;
}

interface HeaderProps {
  t: TFunction;
  totalActivationTasks: number;
  progressPercentage: number;
  progressLabel: string;
}

interface ActivationTaskCardWrapperProps {
  task: Task;
  t: TFunction;
  checkedActivationTasksMap: Map<ActivationTasksTypes, boolean>;
  onCheckboxChange: (step: ActivationTasksTypes, checked: boolean) => void;
}

interface CategoryAccordionWrapperProps {
  category: Category;
  t: TFunction;
  isAccessAllowed: (permissions: Permission[]) => boolean;
  categoryTotalActivationTasksMap: Map<string, number>;
  categoryCheckedActivationTasksMap: Map<string, number>;
  checkedActivationTasksMap: Map<ActivationTasksTypes, boolean>;
  onCheckboxChange: (step: ActivationTasksTypes, checked: boolean) => void;
}

const FinishedContent = memo(({ t, onGoBackToTour, onFinishOnboarding }: FinishedContentProps) => {
  const [, setPreferences] = useLocalStorage<LocalUserPreferences>('localUserPreferences');

  const handleFinishOnboarding = useCallback(() => {
    setPreferences((prev) => ({
      ...prev,
      onboardingCompleted: true,
    }));
    onFinishOnboarding();
  }, [setPreferences, onFinishOnboarding]);

  return (
    <div className='mx-auto flex flex-col items-center justify-center gap-2 py-8'>
      <div className='rounded-full bg-primary-4 p-4'>
        <Icon name='party-popper' className='text-primary-8' size='2xl' />
      </div>
      <div className='flex max-w-md flex-col items-center justify-center gap-2'>
        <Typography variant='text-lg-semibold' color='secondary'>
          {t('onboarding_activation_tasks.finished.title')}
        </Typography>
        <Typography variant='text-md' color='secondary' className='text-center'>
          {t('onboarding_activation_tasks.finished.description')}
        </Typography>
      </div>
      <div className='flex flex-row gap-2'>
        <Button variant='ghost' onClick={onGoBackToTour}>
          {t('onboarding_activation_tasks.finished.go_back_to_tour')}
        </Button>
        <Button variant='contained' onClick={handleFinishOnboarding}>
          {t('onboarding_activation_tasks.finished.finished_button')}
        </Button>
      </div>
    </div>
  );
});

FinishedContent.displayName = 'FinishedContent';

const Header = memo(({ t, totalActivationTasks, progressPercentage, progressLabel }: HeaderProps) => {
  return (
    <div className='-mx-4 flex flex-shrink-0 flex-col gap-1 border-gray-6 border-b px-8 pb-4'>
      <Typography variant='text-lg-semibold'>{t('onboarding_activation_tasks.popover.title')}</Typography>
      <Typography variant='text-md' color='secondary' className='mb-4'>
        {t('onboarding_activation_tasks.popover.subtitle_1')}
        <br />
        {t('onboarding_activation_tasks.popover.subtitle_2')}
      </Typography>
      <ProgressBar value={totalActivationTasks} progress={progressPercentage} label={progressLabel} />
    </div>
  );
});

Header.displayName = 'Header';

const ActivationTaskCardWrapper = memo(({ task, t, checkedActivationTasksMap, onCheckboxChange }: ActivationTaskCardWrapperProps) => {
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onCheckboxChange(task.name, checked);
    },
    [task.name, onCheckboxChange]
  );

  return (
    <ActivationTaskCard
      title={t(`onboarding_activation_tasks.steps.${task.i18nKey}.title`)}
      description={t(`onboarding_activation_tasks.steps.${task.i18nKey}.description`)}
      url={task.url}
      step={task.name}
      checked={checkedActivationTasksMap.get(task.name) ?? false}
      onCheckboxChange={handleCheckboxChange}
      stepId={task.id}
    />
  );
});

ActivationTaskCardWrapper.displayName = 'ActivationTaskCardWrapper';

const CategoryAccordionWrapper = memo(
  ({
    category,
    t,
    isAccessAllowed,
    categoryTotalActivationTasksMap,
    categoryCheckedActivationTasksMap,
    checkedActivationTasksMap,
    onCheckboxChange,
  }: CategoryAccordionWrapperProps) => {
    const allowedActivationTasksInCategory = useMemo(() => {
      return category.tasks.filter((task) => isAccessAllowed(task.permissions as Permission[]));
    }, [category.tasks, isAccessAllowed]);

    return (
      <CategoryAccordion
        key={category.i18nKey}
        title={t(`onboarding_activation_tasks.categories.${category.i18nKey}.title`)}
        description={t(`onboarding_activation_tasks.categories.${category.i18nKey}.description`)}
        totalActivationTasks={categoryTotalActivationTasksMap.get(category.i18nKey) ?? 0}
        checkedActivationTasks={categoryCheckedActivationTasksMap.get(category.i18nKey) ?? 0}
      >
        {allowedActivationTasksInCategory.map((step) => (
          <ActivationTaskCardWrapper key={step.i18nKey} task={step} t={t} checkedActivationTasksMap={checkedActivationTasksMap} onCheckboxChange={onCheckboxChange} />
        ))}
      </CategoryAccordion>
    );
  }
);

CategoryAccordionWrapper.displayName = 'CategoryAccordionWrapper';

const OnboardingActivationTask = ({ initialCheckedActivationTasks, onFinishOnboarding }: OnboardingActivationTaskProps) => {
  const { t } = useTranslation('common');
  const { isAccessAllowed } = usePermissions();

  const onboardingActivationTasksWithConfig = useMemo(() => {
    return getOnboardingTaskConfiguration();
  }, []);

  const [checkedActivationTasks, setCheckedActivationTasks] = useState<ActivationTasksTypes[]>([]);
  const [isFinished, setIsFinished] = useState(false);

  const allowedActivationTasks = useMemo(() => {
    const allowed: ActivationTasksTypes[] = [];
    for (const category of onboardingActivationTasksWithConfig) {
      for (const task of category.tasks) {
        if (isAccessAllowed(task.permissions as Permission[])) {
          allowed.push(task.name);
        }
      }
    }
    return allowed;
  }, [isAccessAllowed]);

  const totalActivationTasks = useMemo(() => allowedActivationTasks.length, [allowedActivationTasks]);

  const progressPercentage = useMemo(() => {
    if (totalActivationTasks === 0) return 0;
    return (checkedActivationTasks.length / totalActivationTasks) * 100;
  }, [checkedActivationTasks.length, totalActivationTasks]);

  const progressLabel = useMemo(() => `${checkedActivationTasks.length}/${totalActivationTasks}`, [checkedActivationTasks.length, totalActivationTasks]);

  const checkedActivationTasksMap = useMemo(() => {
    const map = new Map<ActivationTasksTypes, boolean>();
    for (const step of checkedActivationTasks) {
      map.set(step, true);
    }
    return map;
  }, [checkedActivationTasks]);

  const categoryTotalActivationTasksMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const category of onboardingActivationTasksWithConfig) {
      const totalCount = category.tasks.filter((innerTask) => isAccessAllowed(innerTask.permissions as Permission[])).length;
      map.set(category.i18nKey, totalCount);
    }
    return map;
  }, [isAccessAllowed]);

  const categoryCheckedActivationTasksMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const category of onboardingActivationTasksWithConfig) {
      const checkedCount = category.tasks
        .filter((innerTask) => isAccessAllowed(innerTask.permissions as Permission[]))
        .filter((innerTask) => checkedActivationTasksMap.get(innerTask.name)).length;
      map.set(category.i18nKey, checkedCount);
    }
    return map;
  }, [checkedActivationTasksMap, isAccessAllowed]);

  const allowedCategories = useMemo(() => {
    return onboardingActivationTasksWithConfig.filter((category) => {
      const totalActivationTasksInCategory = categoryTotalActivationTasksMap.get(category.i18nKey) ?? 0;
      return totalActivationTasksInCategory > 0;
    });
  }, [categoryTotalActivationTasksMap]);

  const handleCheckboxChange = useCallback(
    (step: ActivationTasksTypes, checked: boolean) => {
      Analytics.track('Onboarding Checklist - ActivationTask checkbox changed', { step: step, checked: checked });
      setCheckedActivationTasks((prev) => {
        if (checked) {
          if (totalActivationTasks === prev.length + 1) {
            setIsFinished(true);
          }

          return prev.includes(step) ? prev : [...prev, step];
        }
        return prev.filter((s) => s !== step);
      });
    },
    [totalActivationTasks]
  );

  useEffect(() => {
    setCheckedActivationTasks(initialCheckedActivationTasks || []);
    setIsFinished(initialCheckedActivationTasks.length === totalActivationTasks);
  }, [initialCheckedActivationTasks, totalActivationTasks]);

  return (
    <>
      {/* Fixed header section */}
      <Header t={t} totalActivationTasks={totalActivationTasks} progressPercentage={progressPercentage} progressLabel={progressLabel} />

      {/* Finished content */}
      {isFinished && <FinishedContent t={t} onGoBackToTour={() => setIsFinished(false)} onFinishOnboarding={onFinishOnboarding} />}

      {/* Scrollable steps section */}
      <div className={`w-full flex-1 overflow-y-auto p-4 ${isFinished ? 'hidden' : ''}`}>
        <div className='flex flex-col gap-[16px]'>
          {allowedCategories.map((category) => (
            <CategoryAccordionWrapper
              key={category.i18nKey}
              category={category}
              t={t}
              isAccessAllowed={isAccessAllowed}
              categoryTotalActivationTasksMap={categoryTotalActivationTasksMap}
              categoryCheckedActivationTasksMap={categoryCheckedActivationTasksMap}
              checkedActivationTasksMap={checkedActivationTasksMap}
              onCheckboxChange={handleCheckboxChange}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default OnboardingActivationTask;
